import { useParams, usePathname } from 'next/navigation';
import useTranslation from 'next-translate/useTranslation';

import { PopoverButton } from '@headlessui/react';
import clsx from 'clsx';

import useSPGroups from '@/utils/hooks/swr/group/useSPGroups';

import { getIsHeaderManageProfilesDisabled } from '@/utils/layout/getIsHeaderManageProfilesDisabled';
import { useProfilesStore } from '@/utils/zustand/providers/profiles-provider';

import { ButtonStyle } from '@/components/base/Button';
import Icon from '@/components/base/Icon';
import type { PopoverChildrenProps } from '@/components/base/Popover';
import Tooltip from '@/components/base/Tooltip';

type RecapSocialProfilesProps = Pick<PopoverChildrenProps, 'setReferenceElement'>;

export function RecapSocialProfiles({ setReferenceElement }: RecapSocialProfilesProps) {
    const { t } = useTranslation('layout_platform');
    const pathname = usePathname();
    const params = useParams();
    const isDisabled = getIsHeaderManageProfilesDisabled({ pathname, params });
    const { activatedSpGroups } = useSPGroups({ revalidateIfStale: false });
    const { activeGroupId, activeMultiSelectionProfiles } = useProfilesStore((state) => state);

    const numberSelectedProfiles = () => {
        if (activeMultiSelectionProfiles?.socialProfileIds) {
            return activeMultiSelectionProfiles.socialProfileIds.length;
        }

        if (activeGroupId) {
            return activatedSpGroups
                .find((group) => group.id === activeGroupId)
                ?.social_profiles.filter((profile) => profile.active).length;
        }

        return 1;
    };

    return (
        <Tooltip disabled={!isDisabled} content={t('manage_profiles.tooltip_disabled')}>
            <span>
                <PopoverButton
                    ref={setReferenceElement}
                    disabled={isDisabled}
                    data-testid="button-manage-profiles-popover"
                    className={clsx(
                        [
                            'flex !min-w-fit items-center gap-1 focus:ring-offset-0 sm:gap-2',
                            'h-8 px-2 py-2 sm:h-12 sm:px-4',
                            'disabled:opacity-40'
                        ],
                        {
                            [ButtonStyle['whiteNoBorder']]: isDisabled,
                            [ButtonStyle['whiteOpacity40']]: !isDisabled
                        }
                    )}>
                    <Icon icon="single-profile" className="text-icoBlue" />
                    <span className="flex items-center gap-1">
                        <span className="hidden text-xs text-icoBlack dark:text-white sm:inline">
                            {t('manage_profiles.button_popover')}
                        </span>
                        <span className="flex h-4 min-w-[20px] items-center justify-center rounded-full bg-icoBlue px-1 text-xxs leading-none text-white">
                            {numberSelectedProfiles()}
                        </span>
                    </span>
                    <Icon
                        icon="chevron-bottom"
                        className="text-xs text-icoGray-400 dark:text-icoDarkMode-wolf"
                    />
                </PopoverButton>
            </span>
        </Tooltip>
    );
}
